import { DateTime } from 'luxon'
import { SectionPaymentMethod } from '../types'

// The rules behind a section's billing settings (BL-5134). The Billing & Access
// tab uses them to explain what it allows, and the server's billing endpoints
// use them to refuse anything else, so both sides always agree.

/** Methods that sell a section pass (codeless included). */
export const PASS_METHODS: ReadonlySet<SectionPaymentMethod> = new Set([
  SectionPaymentMethod.sectionPassStripe,
  SectionPaymentMethod.sectionPassCoupon,
  SectionPaymentMethod.sectionPassStripeCoupon,
  SectionPaymentMethod.sectionPassCodelessAccess,
])

/** Methods that sell the pass through Stripe (codeless included). */
export const STRIPE_METHODS: ReadonlySet<SectionPaymentMethod> = new Set([
  SectionPaymentMethod.sectionPassStripe,
  SectionPaymentMethod.sectionPassStripeCoupon,
  SectionPaymentMethod.sectionPassCodelessAccess,
])

/** Methods that let participants in with an access code. */
export const CODE_METHODS: ReadonlySet<SectionPaymentMethod> = new Set([
  SectionPaymentMethod.sectionPassCoupon,
  SectionPaymentMethod.sectionPassStripeCoupon,
])

/** While the pass is locked, only these two can swap (codes on or off). */
/** The most CX exception slots one section can have on top of its pass. */
export const MAX_EXTRA_ASSIGNMENTS = 100

export const UNLOCKED_WHILE_LOCKED: ReadonlySet<SectionPaymentMethod> = new Set(
  [
    SectionPaymentMethod.sectionPassStripe,
    SectionPaymentMethod.sectionPassStripeCoupon,
  ]
)

export const MIN_PASS_PRICE_CENTS = 100

/** Max assignments a pass can have. -1 is unlimited. */
export const PASS_QUANTITY_OPTIONS: readonly number[] = [
  -1, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 30, 40, 50,
]

export function isAllowedPassQuantity(quantity: number) {
  return PASS_QUANTITY_OPTIONS.includes(quantity)
}

/**
 * Whether the section pass is locked. It locks once the section has
 * participants, is more than 7 days past its start, and its first assignment
 * has gone out. Staff who can always apply Approved Free bypass it.
 */
export function isSectionPassLocked({
  userCount,
  sectionStartDate,
  assignmentsAssignedAt,
  canBypass,
  now = DateTime.now(),
}: {
  userCount: number
  sectionStartDate: Date | null | undefined
  assignmentsAssignedAt: (Date | null | undefined)[]
  canBypass: boolean
  now?: DateTime
}): boolean {
  if (canBypass) return false
  if (userCount === 0) return false
  if (
    sectionStartDate &&
    now < DateTime.fromJSDate(sectionStartDate).plus({ days: 7 })
  ) {
    return false
  }
  const earliest = assignmentsAssignedAt.reduce<number>(
    (min, at) => Math.min(min, at?.getTime() ?? Infinity),
    Infinity
  )
  // No assignment has gone out yet (none exist, or none has a date). The old
  // modal locked here by accident: an empty list made an invalid date.
  if (earliest === Infinity) return false
  if (now < DateTime.fromMillis(earliest)) return false
  return true
}

export type AccessMethodChangeProblem =
  /** The pass is locked and this change isn't Stripe <-> Access codes or Stripe. */
  | 'locked'
  /** Entering or leaving Codeless Access without canEditCodelessAccess. */
  | 'codeless_privilege'
  /** A pass method needs a pass price and max assignments first. */
  | 'needs_pass'

/**
 * Checks a change to how participants get in (`sectionPaymentMethod`).
 * Returns undefined when it's allowed.
 */
export function checkAccessMethodChange({
  current,
  next,
  locked,
  canEditCodelessAccess,
  sectionPassPrice,
  sectionPassQuantity,
}: {
  current: SectionPaymentMethod
  next: SectionPaymentMethod
  locked: boolean
  canEditCodelessAccess: boolean
  sectionPassPrice: number | null | undefined
  sectionPassQuantity: number | null | undefined
}): AccessMethodChangeProblem | undefined {
  if (next === current) return undefined
  if (
    locked &&
    !(UNLOCKED_WHILE_LOCKED.has(current) && UNLOCKED_WHILE_LOCKED.has(next))
  ) {
    return 'locked'
  }
  const codeless = SectionPaymentMethod.sectionPassCodelessAccess
  if ((current === codeless || next === codeless) && !canEditCodelessAccess) {
    return 'codeless_privilege'
  }
  const needsPrice =
    STRIPE_METHODS.has(next) && (sectionPassPrice ?? 0) < MIN_PASS_PRICE_CENTS
  const needsQuantity =
    PASS_METHODS.has(next) &&
    next !== codeless &&
    (sectionPassQuantity === undefined || sectionPassQuantity === null)
  if (needsPrice || needsQuantity) return 'needs_pass'
  return undefined
}

/**
 * Which of the listed passes is the section's saved one: the pass whose price
 * and max assignments match the section, preferring the product the section's
 * latest section_product row names. `custom` when nothing matches but the
 * section has a price, `none` when it has no pass at all.
 */
export function resolveSavedPass<
  T extends {
    productId?: string
    sectionPassPrice: number
    sectionPassQuantity: number
  },
>({
  options,
  sectionPassPrice,
  sectionPassQuantity,
  sectionProductId,
}: {
  options: readonly T[]
  sectionPassPrice: number | null | undefined
  sectionPassQuantity: number | null | undefined
  sectionProductId?: string
}): { kind: 'listed'; option: T } | { kind: 'custom' } | { kind: 'none' } {
  const price = sectionPassPrice ?? 0
  const matches = options.filter(
    (o) =>
      o.sectionPassPrice === price &&
      o.sectionPassQuantity === sectionPassQuantity
  )
  const match =
    matches.find((o) => o.productId === sectionProductId) ?? matches[0]
  if (match) return { kind: 'listed', option: match }
  if (price > 0) return { kind: 'custom' }
  return { kind: 'none' }
}

export type FreeAccessProblem =
  | 'required'
  | 'in_the_past'
  | 'not_before_end'
  | 'not_within_month'

/**
 * The latest day a free access window may run to: the day before the section
 * ends, and no later than one month after it starts.
 */
export function freeAccessLatest({
  sectionStartDate,
  sectionEndDate,
}: {
  sectionStartDate: Date | null | undefined
  sectionEndDate: Date | null | undefined
}): DateTime | undefined {
  const bounds: DateTime[] = []
  if (sectionEndDate) {
    bounds.push(DateTime.fromJSDate(sectionEndDate).minus({ days: 1 }))
  }
  if (sectionStartDate) {
    bounds.push(DateTime.fromJSDate(sectionStartDate).plus({ months: 1 }))
  }
  return bounds.length ? DateTime.min(...bounds) : undefined
}

/**
 * Checks a free access window end. It must be today or later, before the
 * section ends, and within a month of the start. Returns undefined when valid.
 */
export function checkFreeAccessUntil({
  until,
  sectionStartDate,
  sectionEndDate,
  now = DateTime.now(),
}: {
  until: DateTime | null | undefined
  sectionStartDate: Date | null | undefined
  sectionEndDate: Date | null | undefined
  now?: DateTime
}): FreeAccessProblem | undefined {
  if (!until) return 'required'
  if (until < now.startOf('day')) return 'in_the_past'
  if (sectionEndDate && until >= DateTime.fromJSDate(sectionEndDate)) {
    return 'not_before_end'
  }
  if (
    sectionStartDate &&
    until > DateTime.fromJSDate(sectionStartDate).plus({ months: 1 })
  ) {
    return 'not_within_month'
  }
  return undefined
}
